/* <countdown-announcement> — live's element logic (the babel-compiled class
   in the global bundles), rewritten as a plain class, behavior 1:1:
   - attribute contract: start-time/end-time ("DD/MM/YYYY HH:MM:SS", local),
     custom-timer ("HH:MM:SS", overrides end-time), remember-visibility,
     redeem-click (handleized code) + redeem-text;
   - click either copies data-copy to the clipboard (2s "copied" state) or
     redirects through /discount/<code>/?redirect=<path>;
   - the running timer persists across pages via sessionStorage every 5s.
   The sessionStorage keys stay byte-verbatim from live (they carry no
   gate-tripping token, and a session spanning the cutover keeps its state). */

/* Every countdown section renders its own <script> tag for this file, so it
   can execute more than once per page (IT PDP: template + header group). A
   top-level class declaration threw "Identifier 'CountdownAnnouncement' has
   already been declared" on the second run; the guard keeps it to one. */
if (!customElements.get('countdown-announcement')) {
  class CountdownAnnouncement extends HTMLElement {
    connectedCallback() {
      this._attachSelectors();
      this._attachListeners();
      this.loadCustomTimer();
      this.loadRemainingTime();
      if (this.customTimer && !this.endTime) {
        this.endTime = this._calculateEndTimeFromCustomTimer(this.customTimer);
      }

      // Function to remember the visibility, once the announcement has been seen
      if (this.getAttribute('remember-visibility') === 'true') {
        if (this.classList.contains('visible')) {
          this.setVisibilityEndTime();
        } else {
          this.checkAndSetVisibility();
        }
      }
      if (this.getAttribute('redeem-click')) {
        this.checkDiscountCodeRedeemed();
      }
      // A hidden bar (page limitation, or a remembered dismissal) never shows
      // its readout, so it skips the 1 s display tick. It keeps the 5 s save:
      // the timer persists as a remaining duration, and without the save the
      // countdown would pause while the visitor is on pages that hide it.
      if (!this.classList.contains('visible')) {
        this.saveIntervalId = setInterval(() => {
          this.saveRemainingTime();
        }, 5000);
        return;
      }
      this.startCountdown();
    }

    _attachSelectors() {
      this.discountCodeContainer = this.querySelector('.code-container');
      this.discountCodeLabel = this.discountCodeContainer.querySelector('.discount-code-label');
      this.copiedTextElement = this.discountCodeContainer.querySelector('.copied-text');
      this.copyIcon = this.discountCodeContainer.querySelector('.icon-copy');

      var customTimerAttr = this.getAttribute('custom-timer');
      this.customTimer = customTimerAttr ? this._parseTimeString(customTimerAttr) : null;
      if (!this.customTimer) {
        var endTimeAttr = this.getAttribute('end-time');
        this.endTime = this._parseDateString(endTimeAttr);
      }

      // Added selectors for days
      this.daysWrap = this.querySelector('.days-wrap .value');
      this.daysWrapContainer = this.querySelector('.days-wrap');
      this.hoursWrap = this.querySelector('.hours-wrap .value');
      this.minutesWrap = this.querySelector('.minutes-wrap .value');
      this.secondsWrap = this.querySelector('.seconds-wrap .value');
    }

    _attachListeners() {
      if (!this.getAttribute('redeem-click')) {
        // copy code function
        this.discountCodeContainer.addEventListener('click', this.onDiscountCodeClick.bind(this));
      } else {
        this.discountCodeContainer.addEventListener('click', this.onRedeemDiscountCode.bind(this));
      }
    }

    _parseDateString(dateString) {
      // Assuming the format is "DD/MM/YYYY HH:MM:SS"
      var parts = dateString.split(/\/|\s|:/);
      return new Date(parts[2], parts[1] - 1, parts[0], parts[3], parts[4], parts[5]);
    }

    _parseTimeString(timeString) {
      // Assuming the format is "HH:MM:SS"
      var parts = timeString.split(':');
      return {
        hours: parseInt(parts[0], 10),
        minutes: parseInt(parts[1], 10),
        seconds: parseInt(parts[2], 10),
      };
    }

    _calculateEndTimeFromCustomTimer(timer) {
      var hours = timer.hours;
      var minutes = timer.minutes;
      var seconds = timer.seconds;
      var now = new Date();
      return new Date(now.getTime() + hours * 60 * 60 * 1000 + minutes * 60 * 1000 + seconds * 1000);
    }

    async onDiscountCodeClick(e) {
      e.preventDefault();
      e.stopPropagation();
      var discountCode = this.discountCodeContainer.getAttribute('data-copy');
      try {
        await navigator.clipboard.writeText(discountCode);
        this.discountCodeContainer.classList.add('copied');
        setTimeout(() => {
          this.discountCodeContainer.classList.remove('copied');
        }, 2000);
      } catch (err) {
        console.error('Failed to copy text: ', err);
      }
    }

    onRedeemDiscountCode() {
      var discountCode = this.getAttribute('redeem-click');
      var expirationTime = this.endTime.getTime();
      if (typeof Storage !== 'undefined') {
        sessionStorage.setItem('edCountdownDiscountRedeemed', discountCode);
        sessionStorage.setItem('edCountdownDiscountExpiration', expirationTime);
      }

      // Redirect
      var currentUrl = window.location.href;
      var currentPath = new URL(currentUrl).pathname;
      window.location.href = '/discount/'.concat(discountCode, '/?redirect=').concat(currentPath);
    }

    checkDiscountCodeRedeemed() {
      var redeemText = this.getAttribute('redeem-text');
      var discountRedeemed = sessionStorage.getItem('edCountdownDiscountRedeemed');
      var currentTime = Date.now();
      var expirationTimeStr = sessionStorage.getItem('edCountdownDiscountExpiration');
      var expirationTime = new Date(parseInt(expirationTimeStr, 10));
      if (discountRedeemed && currentTime < expirationTime) {
        // replace discount code Text
        if (this.discountCodeLabel) {
          this.discountCodeLabel.textContent = redeemText;
        }
      }
    }

    startCountdown() {
      this._updateCountdown();
      this.intervalId = setInterval(() => {
        this._updateCountdown();
      }, 1000);
      this.saveIntervalId = setInterval(() => {
        this.saveRemainingTime();
      }, 5000);
    }

    _updateCountdown() {
      var now = new Date();
      var timeRemaining = this.endTime - now;
      if (timeRemaining <= 0) {
        clearInterval(this.intervalId);
        clearInterval(this.saveIntervalId);
        this._setCountdownValues(0, 0, 0, 0);
        sessionStorage.removeItem('edVisibilityEndTime');
        return;
      }
      var totalSeconds = Math.floor(timeRemaining / 1000);
      var days = Math.floor(totalSeconds / (24 * 3600));
      var hours = Math.floor((totalSeconds % (24 * 3600)) / 3600);
      var minutes = Math.floor((totalSeconds % 3600) / 60);
      var seconds = totalSeconds % 60;
      if (days > 0) {
        this.daysWrapContainer.style.display = 'inline-block';
      } else {
        this.daysWrapContainer.style.display = 'none';
      }
      this._setCountdownValues(days, hours, minutes, seconds);
    }

    _setCountdownValues(days, hours, minutes, seconds) {
      if (days > 0) {
        this.daysWrap.textContent = String(days).padStart(2, '0');
      }
      this.hoursWrap.textContent = String(hours).padStart(2, '0');
      this.minutesWrap.textContent = String(minutes).padStart(2, '0');
      this.secondsWrap.textContent = String(seconds).padStart(2, '0');
    }

    saveRemainingTime() {
      if (this.endTime) {
        var now = new Date();
        var timeRemaining = this.endTime - now;
        if (timeRemaining > 0) {
          sessionStorage.setItem('edRemainingTime', timeRemaining);
        } else {
          sessionStorage.removeItem('edRemainingTime');
        }
      }
      if (this.customTimer) {
        sessionStorage.setItem('edCustomTimer', JSON.stringify(this.customTimer));
      }
    }

    loadRemainingTime() {
      var remainingTime = sessionStorage.getItem('edRemainingTime');
      if (remainingTime) {
        var now = new Date();
        this.endTime = new Date(now.getTime() + parseInt(remainingTime, 10));
      } else if (this.customTimer) {
        this.endTime = this._calculateEndTimeFromCustomTimer(this.customTimer);
      }
    }

    loadCustomTimer() {
      // method to observer timer changes. if so, remove/reset the timer
      var storedCustomTimer = sessionStorage.getItem('edCustomTimer');
      if (storedCustomTimer) {
        var parsedStoredCustomTimer = JSON.parse(storedCustomTimer);
        var customTimerAttr = this.getAttribute('custom-timer');
        var currentCustomTimer = customTimerAttr ? this._parseTimeString(customTimerAttr) : null;
        if (JSON.stringify(parsedStoredCustomTimer) !== JSON.stringify(currentCustomTimer)) {
          sessionStorage.removeItem('edRemainingTime');
          this.customTimer = currentCustomTimer;
        } else {
          this.customTimer = parsedStoredCustomTimer;
        }
      } else {
        var customTimerAttrFallback = this.getAttribute('custom-timer');
        this.customTimer = customTimerAttrFallback ? this._parseTimeString(customTimerAttrFallback) : null;
      }
    }

    /** REMEMBER VISIBILTIY FUNCTIONS **/

    checkAndSetVisibility() {
      var visibilityEndTime = sessionStorage.getItem('edVisibilityEndTime');
      if (visibilityEndTime) {
        var now = new Date().getTime();
        if (now < parseInt(visibilityEndTime, 10) && !this.classList.contains('visible')) {
          this.classList.add('visible');
        } else {
          sessionStorage.removeItem('edVisibilityEndTime');
        }
      }
    }

    setVisibilityEndTime() {
      var endTime;
      if (this.customTimer) {
        var hours = this.customTimer.hours;
        var minutes = this.customTimer.minutes;
        var seconds = this.customTimer.seconds;
        endTime = new Date().getTime() + (hours * 3600 + minutes * 60 + seconds) * 1000;
      } else if (this.endTime) {
        endTime = this.endTime.getTime();
      }
      if (endTime) {
        sessionStorage.setItem('edVisibilityEndTime', endTime.toString());
      }
    }

    /** END **/
  }

  customElements.define('countdown-announcement', CountdownAnnouncement);
}
